import { getStudentClassrooms, statutstudents, applyotherpayment, createAndSetPaymentStatus } from './services';
import api_config from '../config';
import { reactive, ref } from 'vue';

class StudentsProvider {
  constructor() {
    this.students = reactive(new Map()); // Cache des étudiants par classroom_id
    this.loading = ref(false);
    this.lastFetch = new Map(); // Timestamp du dernier fetch par classroom
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  }

  // Récupère les étudiants (avec cache)
  async getStudents(classroomId, forceRefresh = false) {
    const cacheKey = classroomId;
    const now = Date.now();
    const lastFetchTime = this.lastFetch.get(cacheKey) || 0;
    
    // Vérifier si on a besoin de refetch
    const needsFetch = forceRefresh || 
                      !this.students.has(cacheKey) || 
                      (now - lastFetchTime) > this.CACHE_DURATION;

    if (!needsFetch) {
      return this.students.get(cacheKey);
    }

    try {
      this.loading.value = true;
      const params = { classroom_id: classroomId };
      const res = await getStudentClassrooms(params);
      
      const studentsData = {
        students: res.data.students || [],
        classroom: res.data.classroom || {},
        payments: res.data.students?.payments || []
      };

      // Mettre à jour le cache
      this.students.set(cacheKey, studentsData);
      this.lastFetch.set(cacheKey, now);
      
      return studentsData;
    } catch (error) {
      console.error('Error fetching students:', error);
      throw error;
    } finally {
      this.loading.value = false;
    }
  }

  // Met à jour le statut des étudiants — crée un paiement en attente si absent
  async updateStudentStatus(classroomId, registrationNumbers, status, installmentId) {
    try {
      this.loading.value = true;

      // Assurer que le cache local est chargé
      let cachedData = this.students.get(classroomId);
      if (!cachedData) {
        cachedData = await this.getStudents(classroomId, true);
      }

      const toCreateIds = [];
      const toUpdateRegistrationNumbers = [];

      // Construire une map pour accès rapide
      const studentsMap = new Map();
      (cachedData.students || []).forEach((s) => {
        if (s.registration_number) studentsMap.set(String(s.registration_number), s);
        if (s.id) studentsMap.set(String(s.id), s);
      });

      for (const reg of registrationNumbers) {
        const student = studentsMap.get(String(reg));
        if (!student) {
          // Si inconnu localement, essayer l'ajout à la mise à jour
          toUpdateRegistrationNumbers.push(reg);
          continue;
        }

        const hasPaymentForInstallment = Array.isArray(student.payments) && student.payments.some((p) => {
          return String(p.installment_id) === String(installmentId) || String(p.id) === String(installmentId);
        });

        if (!hasPaymentForInstallment) {
          // Ajouter l'identifiant interne si disponible, sinon la registration_number
          toCreateIds.push(student.id || student.registration_number);
        } else {
          toUpdateRegistrationNumbers.push(student.registration_number);
        }
      }

      let finalResult = { status: 200 };

      // 1) Créer les paiements côté serveur pour les étudiants sans paiement
      if (toCreateIds.length > 0 && installmentId) {
        const createData = {
          installment_id: installmentId,
          student_id_list: toCreateIds,
        };
        const createRes = await createAndSetPaymentStatus(createData);
        if (!(createRes.status === 200 || createRes.status === 201)) {
          throw new Error('Failed to create payments');
        }

        // Mise à jour locale minimale
        (cachedData.students || []).forEach((s) => {
          if (toCreateIds.includes(s.id) || toCreateIds.includes(s.registration_number)) {
            s.payment_status = 'Pending';
            s.payments = s.payments || [];
            s.payments.push({ installment_id: installmentId, status: 'Pending' });
          }
        });
      }

      // 2) Mettre à jour les statuts des paiements existants via l'endpoint existant
      if (toUpdateRegistrationNumbers.length > 0) {
        const data = {
          registration_number_list: toUpdateRegistrationNumbers,
          classroom_id: classroomId,
          payment_status: status,
          installment_id: installmentId,
        };
        const res = await statutstudents(data);
        if (res.status === 200) {
          await this.updateLocalCache(classroomId, toUpdateRegistrationNumbers, status, installmentId);
          finalResult = res;
        } else {
          throw new Error('Failed to update status for existing payments');
        }
      }

      // Forcer la réactivité du cache
      this.students.set(classroomId, { ...cachedData });
      return finalResult;
    } catch (error) {
      console.error('Error updating student status:', error);
      throw error;
    } finally {
      this.loading.value = false;
    }
  }

  // Met à jour le statut des autres paiements
  async updateOtherPaymentStatus(classroomId, registrationNumbers, status, subscriptionId) {
    try {
      this.loading.value = true;
      
      const data = {
        registration_number_list: registrationNumbers,
        classroom_id: classroomId,
        status: status,
        subscription_id: subscriptionId,
        payment_type: api_config.PAYMENT_TYPE,
      };

      const res = await applyotherpayment(data);
      
      if (res.status === 200) {
        // Mettre à jour le cache local pour les autres paiements
        await this.updateOtherPaymentLocalCache(classroomId, registrationNumbers, status, subscriptionId);
        return res;
      }
      
      throw new Error('Failed to update other payment status');
    } catch (error) {
      console.error('Error updating other payment status:', error);
      throw error;
    } finally {
      this.loading.value = false;
    }
  }

  // Met à jour le cache local après modification
  async updateLocalCache(classroomId, registrationNumbers, newStatus, installmentId) {
    const cachedData = this.students.get(classroomId);
    if (!cachedData) return;

    // Mettre à jour les étudiants concernés dans le cache
    cachedData.students.forEach(student => {
      if (registrationNumbers.includes(student.registration_number)) {
        // Mettre à jour le statut général de l'étudiant
        student.payment_status = newStatus;
        
        // Mettre à jour les paiements spécifiques si nécessaire
        if (student.payments && installmentId) {
          student.payments.forEach(payment => {
            if (payment.id === installmentId || payment.installment_id === installmentId) {
              payment.status = newStatus;
            }
          });
        }
      }
    });

    // Forcer la réactivité
    this.students.set(classroomId, { ...cachedData });
  }

  // Met à jour le cache local pour les autres paiements
  async updateOtherPaymentLocalCache(classroomId, registrationNumbers, newStatus, subscriptionId) {
    const cachedData = this.students.get(classroomId);
    if (!cachedData) return;

    // Mettre à jour les étudiants concernés dans le cache
    cachedData.students.forEach(student => {
      if (registrationNumbers.includes(student.registration_number)) {
        // Mettre à jour les paiements spécifiques
        if (student.payments && subscriptionId) {
          student.payments.forEach(payment => {
            if (payment.id === subscriptionId && payment.is_other_payment) {
              payment.status = newStatus;
            }
          });
        }
      }
    });

    // Forcer la réactivité
    this.students.set(classroomId, { ...cachedData });
  }

  // Invalide le cache pour une classe
  invalidateCache(classroomId) {
    this.students.delete(classroomId);
    this.lastFetch.delete(classroomId);
  }

  // Invalide tout le cache
  clearCache() {
    this.students.clear();
    this.lastFetch.clear();
  }

  // Obtient les étudiants en attente
  getPendingStudents(classroomId) {
    const data = this.students.get(classroomId);
    if (!data || !data.students) return [];
    
    return data.students.filter(student => 
      student.payment_status === 'Pending'
    );
  }

  // Obtient les étudiants réglés
  getSettledStudents(classroomId) {
    const data = this.students.get(classroomId);
    if (!data || !data.students) return [];
    
    return data.students.filter(student => 
      student.payment_status === 'Settled'
    );
  }

  // Obtient les étudiants en retard
  getOverdueStudents(classroomId) {
    const data = this.students.get(classroomId);
    if (!data || !data.students) return [];
    
    const today = new Date();
    return data.students.filter(student => {
      if (student.payment_status !== 'Pending') return false;
      
      // Vérifier si l'étudiant a des paiements en retard
      return student.payments && student.payments.some(payment => 
        payment.status === 'Pending' && 
        payment.due_date && 
        new Date(payment.due_date) < today
      );
    });
  }

  // ✅ Obtient les statistiques des paiements (CORRIGÉ)
  getPaymentStats(classroomId) {
    const data = this.students.get(classroomId);
    if (!data || !data.students) {
      return {
        total: 0,
        settled: 0,
        pending: 0,
        overdue: 0,
        noStatus: 0
      };
    }

    const stats = {
      total: data.students.length,
      settled: 0,
      pending: 0,
      overdue: 0,
      noStatus: 0
    };

    const today = new Date();

    data.students.forEach(student => {
      switch (student.payment_status) {
        case 'Settled': {
          stats.settled++;
          break;
        }
        case 'Pending': {
          // Vérifier si en retard
          const hasOverduePayments = student.payments && student.payments.some(payment => 
            payment.status === 'Pending' && 
            payment.due_date && 
            new Date(payment.due_date) < today
          );
          
          if (hasOverduePayments) {
            stats.overdue++;
          } else {
            stats.pending++;
          }
          break;
        }
        default: {
          stats.noStatus++;
          break;
        }
      }
    });

    return stats;
  }
}

// Instance singleton
export const studentsProvider = new StudentsProvider();
